import React, { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import CircleCheckFill from '@gravity-ui/icons/CircleCheckFill';
import PencilToSquare from '@gravity-ui/icons/PencilToSquare';
import { Button, Icon } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { SettingPageLayout } from 'client/blocks/Layout/Layout';
import { SideMenu } from 'client/blocks/Menu/Menu';
import { routes } from 'client/lib/routes';
import { AliceAgentAppDTO } from 'client/model/aliceAgentApp';
import * as accessKeyset from 'i18n/AliceAgentAppAccess';
import * as formKeyset from 'i18n/AliceAgentAppForm';

import styles from './AliceAgentAppEditPage.module.scss';

const tForm = i18n(formKeyset);
const tAccess = i18n(accessKeyset);

interface Props {
    app: AliceAgentAppDTO;
    isDraft: boolean;
    children: ReactNode;
}

/**
 * Shared layout for every Internal-agent tab: sidebar (logo + version switcher + side menu) on
 * the left, big content header + tab body on the right. Mirrors AliceExternalAppLayout structure
 * so that navigating between Settings and Sharing keeps the surrounding page shell stable.
 *
 * Tab body is passed in via `children` — the container renders `<Routes>` inside this layout
 * so React Router swaps only the inner content.
 */
export const AliceAgentAppLayout: FC<Props> = ({ app, isDraft, children }) => {
    const navigate = useNavigate();

    const displayName = app.draft.name || tForm('agent-fallback-title');
    const logoUrl = app.draft.logo?.url;
    const hasPublished = Boolean(app.published);

    const navigateToVersion = (draft: boolean) => navigate(routes.app.aliceAgentApp(app.skillId, draft));

    // Internal has no per-section anchors (no MCP, no tools-resources sections),
    // so menuItems is a flat list unlike External which has nested items.
    const menuItems = [
        {
            value: routes.app.aliceAgentApp(app.skillId, isDraft),
            content: tForm('side-menu-settings'),
        },
        {
            value: routes.app.aliceAgentAppSharing(app.skillId),
            content: tAccess('side-menu-sharing'),
        },
    ];

    return (
        <SettingPageLayout>
            <section className={styles.pageContainer}>
                <div className={styles.leftColumn}>
                    <div>
                        <div className={styles.menuHeader}>
                            <div className={styles.menuHeaderNameBlock}>
                                {logoUrl ? (
                                    <div
                                        className={styles.headerLogoSmall}
                                        style={{ backgroundImage: `url(${logoUrl})` }}
                                    />
                                ) : (
                                    <div className={styles.logoPlaceholderSmall}>
                                        {displayName.slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <h4 className={styles.menuHeaderTitle}>{displayName}</h4>
                            </div>
                        </div>
                        <div className={styles.deploymentStatus}>
                            {hasPublished && (
                                <Button
                                    className={clx(
                                        styles.deploymentStatusButton,
                                        !isDraft && styles.deploymentStatusButton_active,
                                    )}
                                    onClick={() => navigateToVersion(false)}
                                    view="flat"
                                >
                                    <span className={styles.deploymentStatusRow}>
                                        <Icon className={styles.deploymentStatusIconPublished} data={CircleCheckFill} />
                                        {tForm('version-published')}
                                    </span>
                                </Button>
                            )}
                            <Button
                                className={clx(
                                    styles.deploymentStatusButton,
                                    isDraft && styles.deploymentStatusButton_active,
                                )}
                                onClick={() => navigateToVersion(true)}
                                view="flat"
                            >
                                <span className={styles.deploymentStatusRow}>
                                    <Icon data={PencilToSquare} />
                                    {tForm('version-draft')}
                                </span>
                            </Button>
                        </div>
                        <SideMenu menuItems={menuItems} />
                    </div>
                </div>
                <div className={styles.pageContent}>
                    <header className={styles.contentHeader}>
                        <div className={styles.contentHeaderInner}>
                            <div className={styles.appTitle}>
                                {logoUrl ? (
                                    <div
                                        className={styles.headerLogoMedium}
                                        style={{ backgroundImage: `url(${logoUrl})` }}
                                    />
                                ) : (
                                    <div className={styles.logoPlaceholderMedium}>
                                        {displayName.slice(0, 1).toUpperCase()}
                                    </div>
                                )}
                                <h1 className={styles.appTitleHeader}>{displayName}</h1>
                            </div>
                        </div>
                    </header>

                    {children}
                </div>
            </section>
        </SettingPageLayout>
    );
};
